import { API_URL, getAuthHeaders } from './config';

export interface Product {
    id: string;
    shop_id: string;
    name: string;
    description?: string;
    category_id?: string;
    base_price: number;
    status: 'draft' | 'published' | 'archived';
    image_url?: string;
    images?: string; // JSON array of additional image URLs
    shop_name?: string;
    shipping_rates?: string;
    allows_international?: number;
    address?: string;
    variant_count?: number;
    min_price?: number;
    manage_stock?: number;
    avg_rating?: number;
    review_count?: number;
    shop_currency?: string;
    is_test_gear?: number;
    total_variant_stock?: number;
}

export interface Shop {
    id: string;
    owner_id: string;
    name: string;
    description?: string;
    logo_url?: string;
    banner_url?: string;
    address?: string;
    phone?: string;
    email?: string;
    website_url?: string;
    opening_hours?: string;
    shipping_rates?: string;
    allows_international?: number;
    allows_pickup?: number;
    city?: string;
    zip_code?: string;
    country?: string;
    latitude?: number;
    longitude?: number;
    sendcloud_public_key?: string;
    sendcloud_secret_key?: string;
    default_shipping_config?: string;
    social_links?: {
        instagram?: string;
        facebook?: string;
        tiktok?: string;
        twitter?: string;
        whatsapp?: string;
        linkedin?: string;
        youtube?: string;
    };
}

export const marketplaceService = {
    requestShop: async (data: any) => {
        const res = await fetch(`${API_URL}/marketplace/shops/request`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) {
            const err = await res.json() as any;
            throw new Error(err.error || "Failed to request shop");
        }
        return res.json();
    },
    getMyShop: async () => {
        const res = await fetch(`${API_URL}/marketplace/shops/my`, {
            headers: getAuthHeaders()
        });
        if (res.status === 204) return null;
        if (!res.ok) throw new Error("Failed to fetch shop status");
        return res.json();
    },
    getProducts: async (shopId: string) => {
        const res = await fetch(`${API_URL}/marketplace/shops/${shopId}/products`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch products");
        return res.json();
    },
    createProduct: async (shopId: string, data: any) => {
        const res = await fetch(`${API_URL}/marketplace/shops/${shopId}/products`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) {
            const err = await res.json() as any;
            throw new Error(err.error || "Failed to create product");
        }
        return res.json();
    },
    updateProduct: async (productId: string, data: any) => {
        const res = await fetch(`${API_URL}/marketplace/products/${productId}`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) throw new Error("Failed to update product");
        return res.json();
    },
    updateStock: async (variantId: string, change_amount: number, reason: string) => {
        const res = await fetch(`${API_URL}/marketplace/variants/${variantId}/stock`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify({ change_amount, reason })
        });
        if (!res.ok) throw new Error("Failed to update stock");
        return res.json();
    },
    onboard: async (shopId: string) => {
        const res = await fetch(`${API_URL}/marketplace/shops/${shopId}/onboard`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify({})
        });
        if (!res.ok) {
            const err = await res.json() as any;
            throw new Error(err.error || "Failed to start onboarding");
        }
        return res.json();
    },
    getShopConnectStatus: async (shopId: string) => {
        const res = await fetch(`${API_URL}/marketplace/shops/${shopId}/status`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch connectivity status");
        return res.json();
    },
    getShopStatus: async (shopId: string) => {
        const res = await fetch(`${API_URL}/marketplace/shops/${shopId}/status`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch shop status");
        return res.json();
    },
    getAllProducts: async () => {
        const res = await fetch(`${API_URL}/public/marketplace/products`);
        if (!res.ok) throw new Error("Failed to fetch marketplace products");
        return res.json();
    },
    getVariants: async (productId: string) => {
        const res = await fetch(`${API_URL}/public/marketplace/products/${productId}/variants`);
        if (!res.ok) throw new Error("Failed to fetch variants");
        return res.json();
    },
    createVariant: async (productId: string, data: any) => {
        const res = await fetch(`${API_URL}/marketplace/products/${productId}/variants`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) throw new Error("Failed to create variant");
        return res.json();
    },
    deleteVariant: async (variantId: string) => {
        const res = await fetch(`${API_URL}/marketplace/variants/${variantId}`, {
            method: 'DELETE',
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to delete variant");
        return res.json();
    },
    updateVariant: async (variantId: string, data: any) => {
        const res = await fetch(`${API_URL}/marketplace/variants/${variantId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) throw new Error("Failed to update variant");
        return res.json();
    },
    checkShopName: async (name: string) => {
        const res = await fetch(`${API_URL}/public/marketplace/check-name?name=${encodeURIComponent(name)}`);
        if (!res.ok) throw new Error("Failed to check shop name");
        return res.json() as Promise<{ available: boolean, name: string }>;
    },
    getShop: async (shopId: string) => {
        const res = await fetch(`${API_URL}/public/marketplace/shops/${shopId}`);
        if (!res.ok) throw new Error("Failed to fetch shop");
        return res.json();
    },
    getProduct: async (productId: string) => {
        const res = await fetch(`${API_URL}/public/marketplace/products/${productId}`);
        if (!res.ok) throw new Error("Failed to fetch product");
        return res.json();
    },
    createCheckoutSession: async (items: any[], shipping_method_id?: string, shipping_country?: string, success_url?: string, cancel_url?: string, coupon_code?: string, shipping_cost?: number, shipping_name?: string) => {
        const res = await fetch(`${API_URL}/marketplace/checkout`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify({ items, shipping_method_id, shipping_country, success_url, cancel_url, coupon_code, shipping_cost, shipping_name })
        });
        if (!res.ok) {
            const err = await res.json() as any;
            throw new Error(err.error || "Checkout failed");
        }
        return res.json();
    },
    validateCoupon: async (code: string) => {
        const res = await fetch(`${API_URL}/public/marketplace/coupons/validate?code=${encodeURIComponent(code)}`);
        if (!res.ok) throw new Error("Failed to validate coupon");
        return res.json();
    },
    getShopOrders: async (shopId: string) => {
        const res = await fetch(`${API_URL}/marketplace/shops/${shopId}/orders`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch shop orders");
        return res.json();
    },
    updateSubOrder: async (subOrderId: string, data: any) => {
        const res = await fetch(`${API_URL}/marketplace/sub-orders/${subOrderId}`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) throw new Error("Failed to update order");
        return res.json();
    },
    refuseOrder: async (subOrderId: string, reason: string) => {
        const res = await fetch(`${API_URL}/marketplace/sub-orders/${subOrderId}/refuse`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify({ reason })
        });
        if (!res.ok) {
            const contentType = res.headers.get("content-type");
            if (contentType && contentType.includes("application/json")) {
                const err = await res.json() as any;
                throw new Error(err.error || "Failed to refuse order");
            } else {
                const text = await res.text();
                throw new Error(text || "Failed to refuse order");
            }
        }
        return res.json();
    },
    getMyOrders: async () => {
        const res = await fetch(`${API_URL}/marketplace/orders/my`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch my orders");
        return res.json();
    },
    manualFulfillment: async (sessionId: string) => {
        const res = await fetch(`${API_URL}/marketplace/checkout/manual-fulfillment`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify({ sessionId })
        });
        if (!res.ok) {
            const err = await res.json() as any;
            throw new Error(err.error || "Manual fulfillment failed");
        }
        return res.json();
    },
    getInventoryStats: async (shopId: string) => {
        const res = await fetch(`${API_URL}/marketplace/shops/${shopId}/inventory/stats`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch inventory stats");
        return res.json();
    },
    getInventoryLogs: async (variantId: string) => {
        const res = await fetch(`${API_URL}/marketplace/variants/${variantId}/inventory/logs`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch inventory logs");
        return res.json();
    },
    getHighVelocityItems: async (shopId: string) => {
        const res = await fetch(`${API_URL}/marketplace/shops/${shopId}/inventory/velocity`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch velocity items");
        return res.json();
    },
    // Suppliers
    getShopSuppliers: async (shopId: string) => {
        const res = await fetch(`${API_URL}/shops/${shopId}/suppliers`, { headers: getAuthHeaders() });
        if (!res.ok) throw new Error("Failed");
        return res.json();
    },
    getVariantSupplierLinks: async (variantId: string) => {
        const res = await fetch(`${API_URL}/products/${variantId}/suppliers`, { headers: getAuthHeaders() });
        if (!res.ok) throw new Error("Failed");
        return res.json();
    },
    addVariantSupplierLink: async (variantId: string, data: any) => {
        const res = await fetch(`${API_URL}/products/${variantId}/suppliers`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify(data)
        });
        if (!res.ok) throw new Error("Failed");
        return res.json();
    },
    removeVariantSupplierLink: async (linkId: string) => {
        const res = await fetch(`${API_URL}/supplier-links/${linkId}`, {
            method: 'DELETE',
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed");
        return res.json();
    },
    getProductReviews: async (productId: string) => {
        const res = await fetch(`${API_URL}/marketplace/products/${productId}/reviews`);
        if (!res.ok) throw new Error("Failed to fetch reviews");
        return res.json();
    },
    createProductReview: async (productId: string, data: { rating: number, title?: string, comment?: string }) => {
        const res = await fetch(`${API_URL}/marketplace/products/${productId}/reviews`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) {
            const err = await res.json() as any;
            throw new Error(err.error || "Failed to post review");
        }
        return res.json();
    },
    reportProductReview: async (reviewId: string, reason: string) => {
        const res = await fetch(`${API_URL}/marketplace/reviews/${reviewId}/report`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify({ reason })
        });
        if (!res.ok) throw new Error("Failed to report review");
        return res.json();
    },
    getShopReviews: async (shopId: string) => {
        const res = await fetch(`${API_URL}/marketplace/shops/${shopId}/reviews`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch shop reviews");
        return res.json();
    },
    replyToReview: async (reviewId: string, reply_text: string) => {
        const res = await fetch(`${API_URL}/marketplace/reviews/${reviewId}/reply`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify({ reply_text })
        });
        if (!res.ok) throw new Error("Failed to reply to review");
        return res.json();
    },
    acknowledgeReview: async (reviewId: string) => {
        const res = await fetch(`${API_URL}/marketplace/reviews/${reviewId}/acknowledge`, {
            method: 'POST',
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to acknowledge review");
        return res.json();
    },
    generateShippingLabel: async (subOrderId: string, dimensions: { weight: number, width?: number, height?: number, length?: number }) => {
        const res = await fetch(`${API_URL}/marketplace/sub-orders/${subOrderId}/shipping-label`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify(dimensions)
        });
        if (!res.ok) {
            const err = await res.json() as any;
            throw new Error(err.error || "Failed to generate shipping label");
        }
        return res.json();
    }
};
